import * as _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { InputAdornment, MenuItem, TextField } from '@material-ui/core';
import {
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  FileText,
  Headphones,
  Hourglass,
  Minus,
  RefreshCcw,
  Search,
  SlidersHorizontal,
} from 'lucide-react';

import Spinner from '../../spinner';
import { Role } from '../../../shared/constants';
import { NumbersService } from '../../../services/numbers';
import { getErrorMessage } from '../../../shared/services/error-handler';
import {
  getExpenseRequests,
  getInvoiceRequests,
  getSupportTickets,
} from '../../../api/requests/requests';
import { departmentOptions } from '../common/options';
import RequestView from '../request-view';
import '../Requests.scss';

const REQUEST_TYPE = {
  invoice: 'invoice',
  expense: 'expense',
  service: 'service',
};

const tabIds = {
  all: 'all',
  invoices: 'invoices',
  expenses: 'expenses',
  serviceCalls: 'serviceCalls',
  recurring: 'recurring',
};

const STATUS = {
  new: 'new',
  inProgress: 'in_progress',
  done: 'done',
  rejected: 'rejected',
};

const PENDING_STATUSES = [STATUS.new, STATUS.inProgress];
const DATE_OPTIONS = { year: 'numeric', month: 'numeric', day: 'numeric' };
const CURRENCY_SYMBOL = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-GB', DATE_OPTIONS) : '';

const formatAmount = (amount, currency) => {
  if (_.isNil(amount) || amount === '') return '';

  const symbol = CURRENCY_SYMBOL[currency] || `${currency} `;

  return `${symbol}${NumbersService.numberWithCommas(Number(amount), true)}`;
};

const getInvoiceTotal = (invoiceRequest) =>
  _.sumBy(invoiceRequest.items, (item) => {
    const net = Number(item.quantity || 0) * Number(item.unit_price || 0);

    return net + (net * Number(item.vat_rate || 0)) / 100;
  });

const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

const RECURRING_STEP = {
  generated: 'generated',
  inProgress: 'in_progress',
  pending: 'pending',
  skipped: 'skipped',
  next: 'next',
};

const getBillingPeriod = (date) =>
  `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}`;

const getRecurringMonthSteps = (children, now = new Date()) =>
  [-2, -1, 0, 1].map((offset) => {
    const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const period = getBillingPeriod(date);
    const child = _.find(children, { billingPeriod: period });

    let state = RECURRING_STEP.next;

    if (child) {
      state =
        child.status === STATUS.inProgress
          ? RECURRING_STEP.inProgress
          : RECURRING_STEP.generated;
    } else if (offset < 0) {
      state = RECURRING_STEP.skipped;
    } else if (offset === 0) {
      state = RECURRING_STEP.pending;
    }

    return {
      key: period,
      monthKey: MONTH_KEYS[date.getMonth()],
      year: date.getFullYear(),
      state,
    };
  });

const getLatestGeneratedItem = (generatedItems) =>
  _.maxBy(generatedItems, (request) => request.billingPeriod || request.createdAt);

const RecurringInvoiceCard = ({ item, generatedItems = [], t, onClick }) => {
  const day = item.recurringDay || 1;
  const steps = getRecurringMonthSteps(generatedItems);

  return (
    <button
      type="button"
      className="recurring-invoice-card"
      onClick={onClick}
    >
      <div className="recurring-invoice-card-top">
        <div className="recurring-invoice-card-icon">
          <RefreshCcw size={18} />
        </div>

        <div className="recurring-invoice-card-copy">
          <div className="recurring-invoice-card-heading">
            <h3>{item.entityName || item.title}</h3>
            <span className="recurring-status-renewing">
              <span className="recurring-status-dot" />
              {t('recurring-status-renewing')}
            </span>
          </div>

          <div className="recurring-invoice-card-sub">
            <p>{item.title}</p>
            {item.amount && <strong>{item.amount}</strong>}
          </div>

          <p className="recurring-invoice-card-schedule">
            {t('recurring-schedule-day', { day })}
          </p>
        </div>
      </div>

      <div className="recurring-invoice-card-months">
        {steps.map((step) => (
          <div
            key={step.key}
            className={`recurring-month-step ${step.state}`}
          >
            {step.state === RECURRING_STEP.generated && <Check size={16} />}
            {step.state === RECURRING_STEP.inProgress && (
              <span className="recurring-month-step-dot" />
            )}
            {step.state === RECURRING_STEP.pending && <Hourglass size={16} />}
            {step.state === RECURRING_STEP.skipped && <Minus size={16} />}
            {step.state === RECURRING_STEP.next && <ArrowRight size={16} />}
            <strong>{t(`recurring-month-${step.monthKey}`)}</strong>
            <span>{t(`recurring-step-${step.state}`)}</span>
          </div>
        ))}
      </div>
    </button>
  );
};

const getSubmitterName = (request) => {
  const creator = request.creator;

  if (!creator) return '';

  return _.compact([creator.firstname, creator.lastname]).join(' ') || creator.email || '';
};

const MyRequests = (props) => {
  const { user } = props;
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentTabId, setCurrentTabId] = useState(tabIds.all);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [expenseRequests, setExpenseRequests] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);

  const isAdmin = user?.role === Role.admin;
  const isRtl = i18n.dir() === 'rtl';

  useEffect(() => {
    setIsLoading(true);

    Promise.all([
      getInvoiceRequests(),
      getExpenseRequests(),
      getSupportTickets(),
    ])
      .then(([invoices, expenses, tickets]) => {
        setInvoiceRequests(invoices || []);
        setExpenseRequests(expenses || []);
        setSupportTickets(tickets || []);
      })
      .catch((error) => {
        enqueueSnackbar(getErrorMessage(error) || t('something-went-wrong'), {
          variant: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOptionLabel = (options, value) => {
    const option = _.find(options, { value });

    return option ? t(option.tLabel) : value || '';
  };

  const allItems = useMemo(() => {
    const invoices = _.map(invoiceRequests, (request) => ({
      key: `${REQUEST_TYPE.invoice}-${request.id}`,
      id: request.id,
      type: REQUEST_TYPE.invoice,
      status: request.status,
      title: request.services_description,
      entityName: _.get(request, 'client.client_name', ''),
      date: formatDate(request.created_at),
      createdAt: request.created_at,
      amount: formatAmount(getInvoiceTotal(request), request.currency),
      submittedBy: getSubmitterName(request),
      isRecurring: Boolean(request.is_recurring),
      recurringDay: request.recurring_day || null,
      parentId: request.parent_invoice_request_id || null,
      billingPeriod: request.billing_period || null,
    }));

    const expenses = _.map(expenseRequests, (request) => ({
      key: `${REQUEST_TYPE.expense}-${request.id}`,
      id: request.id,
      type: REQUEST_TYPE.expense,
      status: request.status,
      title: request.description,
      entityName: _.get(request, 'supplier.supplier_name', ''),
      date: formatDate(request.expense_date || request.created_at),
      createdAt: request.created_at,
      amount: formatAmount(request.total_amount, request.currency),
      submittedBy: getSubmitterName(request),
      isRecurring: false,
      recurringDay: null,
      parentId: null,
      billingPeriod: null,
    }));

    const tickets = _.map(supportTickets, (ticket) => ({
      key: `${REQUEST_TYPE.service}-${ticket.id}`,
      id: ticket.id,
      type: REQUEST_TYPE.service,
      status: ticket.status,
      title: ticket.subject,
      entityName: getOptionLabel(departmentOptions, ticket.department),
      date: formatDate(ticket.created_at),
      createdAt: ticket.created_at,
      amount: '',
      submittedBy: getSubmitterName(ticket),
      isRecurring: false,
      recurringDay: null,
      parentId: null,
      billingPeriod: null,
    }));

    return _.orderBy(
      [...invoices, ...expenses, ...tickets],
      ['createdAt'],
      ['desc']
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceRequests, expenseRequests, supportTickets, t]);

  const stats = useMemo(
    () => ({
      total: allItems.length,
      pending: allItems.filter((item) => PENDING_STATUSES.includes(item.status)).length,
      completed: allItems.filter((item) => item.status === STATUS.done).length,
      recurring: allItems.filter((item) => item.isRecurring).length,
    }),
    [allItems]
  );

  const tabCounts = useMemo(
    () => ({
      [tabIds.all]: allItems.length,
      [tabIds.invoices]: allItems.filter((item) => item.type === REQUEST_TYPE.invoice).length,
      [tabIds.expenses]: allItems.filter((item) => item.type === REQUEST_TYPE.expense).length,
      [tabIds.serviceCalls]: allItems.filter((item) => item.type === REQUEST_TYPE.service).length,
      [tabIds.recurring]: allItems.filter((item) => item.isRecurring).length,
    }),
    [allItems]
  );

  const visibleItems = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return allItems.filter((item) => {
      const matchesTab =
        currentTabId === tabIds.all ||
        (currentTabId === tabIds.invoices && item.type === REQUEST_TYPE.invoice) ||
        (currentTabId === tabIds.expenses && item.type === REQUEST_TYPE.expense) ||
        (currentTabId === tabIds.serviceCalls && item.type === REQUEST_TYPE.service);

      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesSearch =
        !query ||
        [item.title, item.entityName, item.submittedBy]
          .join(' ')
          .toLowerCase()
          .includes(query);

      return matchesTab && matchesStatus && matchesSearch;
    });
  }, [allItems, currentTabId, searchValue, statusFilter]);

  const regularItems = useMemo(
    () => visibleItems.filter((item) => !item.isRecurring),
    [visibleItems]
  );

  const recurringItems = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const showRecurring =
      currentTabId === tabIds.all ||
      currentTabId === tabIds.invoices ||
      currentTabId === tabIds.recurring;

    if (!showRecurring) return [];

    return allItems.filter((item) => {
      if (!item.isRecurring) return false;

      return (
        !query ||
        [item.title, item.entityName, item.submittedBy]
          .join(' ')
          .toLowerCase()
          .includes(query)
      );
    });
  }, [allItems, currentTabId, searchValue]);

  const tabs = [
    { id: tabIds.all, label: 'all-requests-tab', count: tabCounts[tabIds.all] },
    { id: tabIds.invoices, label: 'invoice-requests-tab', count: tabCounts[tabIds.invoices] },
    { id: tabIds.expenses, label: 'expense-requests-tab', count: tabCounts[tabIds.expenses] },
    { id: tabIds.serviceCalls, label: 'service-calls-tab', count: tabCounts[tabIds.serviceCalls] },
    { id: tabIds.recurring, label: 'recurring-requests-tab', count: tabCounts[tabIds.recurring] },
  ];

  const getTypeIcon = (type) => {
    if (type === REQUEST_TYPE.expense) return <CircleDollarSign size={18} />;
    if (type === REQUEST_TYPE.service) return <Headphones size={18} />;

    return <FileText size={18} />;
  };

  return (
    <div className="my-requests-container">
      <div className="my-requests-header">
        <h1 className="my-requests-title">
          {isAdmin ? t('all-requests-title') : t('my-requests-title')}
        </h1>
        <p className="my-requests-subtitle">
          {isAdmin ? t('all-requests-subtitle') : t('my-requests-subtitle')}
        </p>
      </div>

      {isLoading && <Spinner />}

      {!isLoading && (
        <div className="my-requests-body">
          <div className="my-requests-stats">
            <div className="my-requests-stat">
              <strong>{stats.total}</strong>
              <span>{t('requests-stat-total')}</span>
            </div>
            <div className="my-requests-stat pending">
              <strong>{stats.pending}</strong>
              <span>{t('requests-stat-pending')}</span>
            </div>
            <div className="my-requests-stat completed">
              <strong>{stats.completed}</strong>
              <span>{t('requests-stat-completed')}</span>
            </div>
            <div className="my-requests-stat recurring">
              <strong>{stats.recurring}</strong>
              <span>{t('requests-stat-recurring')}</span>
            </div>
          </div>

          <div className="my-requests-tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={currentTabId === tab.id}
                className={`my-requests-tab ${currentTabId === tab.id ? 'active' : ''}`}
                onClick={() => setCurrentTabId(tab.id)}
              >
                {t(tab.label)}
                <span className="my-requests-tab-count">{tab.count}</span>
              </button>
            ))}
          </div>

          {currentTabId !== tabIds.recurring && (
          <div className="my-requests-toolbar">
            <div className="my-requests-search">
              <Search size={18} />
              <input
                type="text"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder={t('search-requests-placeholder')}
              />
            </div>
            <TextField
              select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              variant="outlined"
              className="my-requests-status-filter"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SlidersHorizontal size={16} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="all">{t('all-statuses')}</MenuItem>
              <MenuItem value={STATUS.new}>{t('request-status-new')}</MenuItem>
              <MenuItem value={STATUS.inProgress}>
                {t('request-status-in_progress')}
              </MenuItem>
              <MenuItem value={STATUS.done}>{t('request-status-done')}</MenuItem>
              <MenuItem value={STATUS.rejected}>
                {t('request-status-rejected')}
              </MenuItem>
            </TextField>
          </div>
          )}

          {regularItems.length > 0 && (
            <div className="my-requests-list">
              {regularItems.map((item) => (
                <button
                  type="button"
                  className={`my-request-card ${item.type}`}
                  key={item.key}
                  onClick={() =>
                    setSelectedRequest({ type: item.type, id: item.id })
                  }
                >
                  <div className={`my-request-card-icon ${item.type}`}>
                    {getTypeIcon(item.type)}
                  </div>

                  <div className="my-request-card-main">
                    <div className="my-request-card-info">
                      <span className={`my-request-status ${item.status}`}>
                        {item.status === STATUS.done && <Check size={14} />}
                        {item.status === STATUS.new && <Hourglass size={14} />}
                        {item.status !== STATUS.done &&
                          item.status !== STATUS.new && (
                            <span className="my-request-status-dot" />
                          )}
                        {t(`request-status-${item.status}`)}
                      </span>
                      <div className="my-request-card-meta">
                        {[item.entityName, item.date, isAdmin ? item.submittedBy : '']
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>

                    <div className="my-request-card-summary">
                      <div className="my-request-card-type-row">
                        <span className={`my-request-type ${item.type}`}>
                          {t(`request-type-${item.type}`)}
                        </span>
                      </div>
                      <div className="my-request-card-title-row">
                        <p className="my-request-card-title">{item.title}</p>
                        {isRtl ? (
                          <ChevronLeft size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </div>
                      {item.amount && (
                        <div className="my-request-card-amount">{item.amount}</div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {recurringItems.length > 0 && (
            <div className="recurring-invoices-section">
              <h2 className="recurring-invoices-heading">
                {t('recurring-invoices-tracking')}
              </h2>
              <div className="my-requests-list">
                {recurringItems.map((item) => {
                  const generatedItems = allItems.filter(
                    (request) => request.parentId === item.id
                  );
                  const latestGenerated = getLatestGeneratedItem(generatedItems);

                  return (
                    <RecurringInvoiceCard
                      key={item.key}
                      item={item}
                      t={t}
                      generatedItems={generatedItems}
                      onClick={() =>
                        setSelectedRequest({
                          type: item.type,
                          id: latestGenerated?.id || item.id,
                          renewalHistory: getRecurringMonthSteps(generatedItems),
                        })
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}

          {regularItems.length === 0 && recurringItems.length === 0 && (
            <div className="no-records-message">{t('no-requests-yet')}</div>
          )}
        </div>
      )}

      <RequestView
        open={Boolean(selectedRequest)}
        type={selectedRequest?.type}
        requestId={selectedRequest?.id}
        renewalHistory={selectedRequest?.renewalHistory}
        onClose={() => setSelectedRequest(null)}
      />
    </div>
  );
};

export default MyRequests;
